import { Transition } from "@headlessui/react";
import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import SafeImage from "components/SafeImage/SafeImage";

interface MegaMenuSubItem {
  id: string;
  name: string;
  href: string;
}

export interface MegaMenuCategory {
  id: string | number;
  image: string;
  title: string;
  items: MegaMenuSubItem[];
}

const isValidSubcategoryName = (name: string) => {
  const trimmed = name?.trim() || "";
  if (trimmed.length < 2) return false;
  if (/^\.+$/.test(trimmed)) return false;
  return true;
};

const splitIntoColumns = <T,>(items: T[], columnCount: number): T[][] => {
  const cols: T[][] = Array.from({ length: columnCount }, () => []);
  items.forEach((item, index) => {
    cols[index % columnCount].push(item);
  });
  return cols;
};

export interface MegaMenuPanelProps {
  categories: MegaMenuCategory[];
}

const MegaMenuPanel: FC<MegaMenuPanelProps> = ({ categories }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | number>(
    categories[0]?.id ?? ""
  );
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    if (categories.length > 0) {
      setActiveId(categories[0].id);
    }
  }, [categories]);

  const updateScrollHints = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setCanScrollUp(scrollTop > 8);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 8);
  }, []);

  useEffect(() => {
    updateScrollHints();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollHints);
    observer.observe(el);
    return () => observer.disconnect();
  }, [categories, updateScrollHints]);

  const columns = useMemo(
    () => splitIntoColumns(categories, 4),
    [categories]
  );

  const activeCategory =
    categories.find((c) => String(c.id) === String(activeId)) ?? categories[0];

  const subItems =
    activeCategory?.items.filter((sub) => isValidSubcategoryName(sub.name)) ??
    [];

  return (
    <div className="flex flex-col lg:flex-row min-h-[340px] max-h-[min(72vh,500px)]">
      <div className="relative flex-1 min-w-0">
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white dark:from-neutral-900 to-transparent z-10 transition-opacity duration-300 ${
            canScrollUp ? "opacity-100" : "opacity-0"
          }`}
        />

        <div
          ref={scrollRef}
          onScroll={updateScrollHints}
          className="h-full overflow-y-auto p-5 sm:p-6 lg:p-7 bcr-scrollbar-hide"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 lg:gap-x-10 gap-y-1">
            {columns.map((column, colIndex) => (
              <div key={colIndex} className="min-w-0">
                <ul className="space-y-0.5">
                  {column.map((category) => {
                    const isActive =
                      String(category.id) === String(activeId);
                    return (
                      <li key={category.id}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveId(category.id)}
                          onFocus={() => setActiveId(category.id)}
                          className={`w-full text-left text-sm py-1.5 px-1 rounded-md transition-all duration-200 ${
                            isActive
                              ? "text-primary-600 dark:text-primary-300 font-semibold bg-primary-50/80 dark:bg-primary-900/20"
                              : "text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 font-normal"
                          }`}
                        >
                          {category.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white dark:from-neutral-900 via-white/90 dark:via-neutral-900/90 to-transparent z-10 transition-opacity duration-300 ${
            canScrollDown ? "opacity-100" : "opacity-0"
          }`}
        />

        {canScrollDown && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-0.5">
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 font-medium">
              Scroll
            </span>
            <ChevronDownIcon className="w-4 h-4 text-neutral-400 dark:text-neutral-500 animate-bounce-scroll" />
          </div>
        )}
      </div>

      <div className="w-full lg:w-[320px] xl:w-[340px] shrink-0 border-t lg:border-t-0 lg:border-l border-neutral-200 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-800/40 overflow-hidden">
        <Transition
          show={!!activeCategory}
          as="div"
          className="h-full"
          enter="transition ease-out duration-300"
          enterFrom="opacity-0 translate-x-4"
          enterTo="opacity-100 translate-x-0"
          leave="transition ease-in duration-200"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 translate-x-2"
        >
          <div
            key={String(activeCategory?.id)}
            className="h-full flex flex-col p-5 sm:p-6 bcr-mega-preview-enter"
          >
            <div className="relative h-36 sm:h-40 rounded-xl overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-700 mb-4">
              <SafeImage
                src={activeCategory?.image}
                alt={activeCategory?.title || "Category"}
                placeholderVariant="product"
                placeholderCompact
                objectFit="contain"
                imgClassName="p-3"
              />
            </div>

            <Link
              to={`/categories/${activeCategory?.id}`}
              className="text-base font-bold text-neutral-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-300 transition-colors leading-snug"
            >
              {activeCategory?.title}
            </Link>

            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500 font-semibold">
              Sub Categories
            </p>

            <ul className="mt-3 space-y-2 flex-1 overflow-y-auto bcr-scrollbar-hide pr-1">
              {subItems.length > 0 ? (
                subItems.map((sub) => (
                  <li key={sub.id}>
                    <NavLink
                      to={sub.href || ""}
                      className="block text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors leading-snug"
                    >
                      {sub.name}
                    </NavLink>
                  </li>
                ))
              ) : (
                <li className="text-sm text-neutral-500 dark:text-neutral-400">
                  Explore products in this category.
                </li>
              )}
            </ul>

            <Link
              to={`/categories/${activeCategory?.id}`}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200 transition-colors"
            >
              View category
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default MegaMenuPanel;
